import _ from 'lodash';
import { gsap } from 'gsap';
import Rellax from 'rellax';
import { scrollToHash, scrollToElement, isMobileOrTablet, isHTMLElement } from '../utils';
import Swiper from 'swiper';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

// 記述が多いのもはimport
import { registerBreakPointEvent } from './registerBreakPointEvent';

/**
 * @function rewriteTellink
 * @description PCの場合、<a href="tel:">を<span>に変更、class等attributeはそのまま継承
 * */
const _rewriteAtag = (el: HTMLElement): void => {
  const attributes = el.attributes;
  const innerHtml = el.innerHTML;
  let attributesText = null;
  for (let i = 0; i < attributes.length; i++) {
    const attribute = attributes[i];
    if (attribute.name != 'href') {
      const t = `${attribute.name}="${attribute.textContent}"`;
      attributesText = attributesText == null ? t : `${t} ${attributesText}`;
    }
  }
  if (attributesText == null) {
    el.outerHTML = `<span>${innerHtml}</span>`;
  } else {
    el.outerHTML = `<span ${attributesText}>${innerHtml}</span>`;
  }
};

const rewriteTellink = () => {
  const tel_links = document.querySelectorAll('a[href^="tel:"]') as NodeListOf<HTMLElement>;
  if (tel_links.length && !isMobileOrTablet()) {
    tel_links.forEach((tel_link) => {
      _rewriteAtag(tel_link);
    });
  }
};

/**
 * @function smoothScroll
 * @description ページ内リンクのスムーススクロール
 * */

const smoothScroll = (): void => {
  const anchor_links = document.querySelectorAll('a[href^="#"]') as NodeListOf<HTMLElement>;
  if (anchor_links.length) {
    anchor_links.forEach((anchor_link) => {
      anchor_link.addEventListener('click', (e) => {
        const hash = anchor_link.getAttribute('href');
        if (hash) {
          scrollToHash(hash, e);
        }
      });
    });
  }
  if (location.hash) {
    scrollToHash(location.hash, null);
  }
};

/**
 * @function adjustViewport
 * @description viewport 375px以下は width=375 で固定にする
 * */
const adjustViewport = (): void => {
  const triggerWidth = 375;
  const viewport = document.querySelector('meta[name="viewport"]');
  const value = window.outerWidth < triggerWidth ? `width=${triggerWidth}, target-densitydpi=device-dpi` : 'width=device-width, initial-scale=1,maximum-scale=1.0';
  if (viewport) {
    viewport.setAttribute('content', value);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = value;
    document.head.appendChild(meta);
  }
};
/**
 * @function setRootProperty
 * @description :root{  }に動的なcss変数を設定
 * */
const setRootProperty = (): void => {
  const root = document.querySelector(':root') as HTMLElement;
  if (root) {
    root.style.setProperty('--view-width', document.body.clientWidth + 'px');
  }
};

/**
 * @function accordionFunc
 */
const accordionFunc = () => {
  const btns = document.querySelectorAll('[data-acdn]') as NodeListOf<HTMLElement>;
  if (btns.length === 0) return;

  const params = new URLSearchParams(window.location.search);
  const acdnOpenId = params.get('acdn-open');
  btns.forEach((btn) => {
    const id = btn.dataset.acdn;
    if (_.isUndefined(id)) return;
    const content = document.getElementById(id);
    if (isHTMLElement(content)) {
      // ★ 初期状態の判定
      const shouldOpen = btn.classList.contains('is-open') || acdnOpenId === id;

      if (!shouldOpen) {
        content.style.height = '0';
      } else {
        btn.classList.add('is-open');
        content.style.height = 'auto';
      }
      content.style.overflow = 'hidden';

      btn.addEventListener('click', () => {
        btn.classList.toggle('is-open');
        if (btn.classList.contains('is-open')) {
          gsap.to(content, {
            height: 'auto',
            duration: 0.3,
          });
        } else {
          gsap.to(content, {
            height: 0,
            duration: 0.3,
            onComplete: () => {
              scrollToElement(btn, null);
            },
          });
        }
      });
    }
  });
};

/**
 * @function toggleGnav
 */
const toggleGnav = () => {
  const toggles = document.querySelectorAll('[data-gnav-toggle]');
  const gnav = document.getElementById('Gnav');
  if (toggles.length === 0 || !isHTMLElement(gnav)) return;
  toggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      gnav.classList.toggle('is-open');
      if (gnav.classList.contains('is-open')) {
        document.body.classList.add('is-nav-open');
      } else {
        document.body.classList.remove('is-nav-open');
      }
    });
  });
};

/**
 * @function rellaxItems
 */
const rellaxItems = (): void => {
  const items = document.querySelectorAll('.js-rellax') as NodeListOf<HTMLElement>;
  if (items.length === 0) return;
  new Rellax('.js-rellax', {
    center: true,
    vertical: true,
  });
};

/**
 * @function fillWindowWidth
 * @description 画面いっぱいに広げるためにネガティブマージンをつける
 * */

const fillWindowWidth = () => {
  const els = document.querySelectorAll('[data-fill-window]') as NodeListOf<HTMLElement>;
  if (els.length === 0) return;
  Array.from(els).forEach((el) => {
    el.style.marginInlineStart = '';
    el.style.marginInlineEnd = '';
    el.style.marginInline = '';
    const rect = el.getBoundingClientRect();

    if (el.dataset.fillWindow == 'left') {
      el.style.marginInlineStart = `-${rect.left}px`;
    } else if (el.dataset.fillWindow == 'right') {
      el.style.marginInlineEnd = `-${document.body.clientWidth - rect.right}px`;
    } else {
      el.style.marginInline = `-${rect.left}px -${document.body.clientWidth - rect.right}px`;
    }
  });
};

/**
 * @function customSwiper
 */
const customSwiper = (): void => {
  const customSwiperEls = document.querySelectorAll('.swiper.custom-swiper') as NodeListOf<HTMLElement>;
  if (customSwiperEls.length === 0) return;
  Array.from(customSwiperEls).forEach((customSwiperEl) => {
    let options: Record<string, any> = {};

    try {
      if (customSwiperEl.dataset.options) {
        options = JSON.parse(customSwiperEl.dataset.options);
      }
    } catch (error) {
      console.warn('Invalid JSON in data-options:', customSwiperEl.dataset.options, error);
      options = {};
    }

    const myS = new Swiper(customSwiperEl, {
      ...{ modules: [Autoplay, Pagination, Navigation, EffectFade] },
      ...options,
    });
  });
};

/**
 * @function megaMenu
 */
const megaMenu = (): void => {
  const navbar = document.querySelector('.l-navbar') as HTMLElement;
  const btns = document.querySelectorAll('[data-megamenu]') as NodeListOf<HTMLElement>;
  navbar.dataset.opened = '';
  if (btns.length === 0 || !navbar) return;
  btns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const megamenu_id = btn.dataset.megamenu!;
      const megamenu = document.getElementById(megamenu_id);
      if (navbar.dataset.opened === '') {
        gsap.to(megamenu, {
          height: 'auto',
          duration: 0.3,
        });
        navbar.dataset.opened = megamenu_id;
      } else if (navbar.dataset.opened === megamenu_id) {
        gsap.to(megamenu, {
          height: 0,
          duration: 0.3,
        });
        navbar.dataset.opened = '';
      } else {
        const opened = document.getElementById(navbar.dataset.opened!);
        gsap.to(megamenu, {
          height: 'auto',
          duration: 0.3,
        });
        gsap.to(opened, {
          height: 0,
          duration: 0.3,
        });
        navbar.dataset.opened = megamenu_id;
      }
    });
  });
};

// export object
const Common = {
  init() {
    accordionFunc();
    registerBreakPointEvent();
    adjustViewport();
    setRootProperty();
    rewriteTellink();
    smoothScroll();
    fillWindowWidth();
    toggleGnav();
    rellaxItems();
    customSwiper();
    megaMenu();
    // PhotoSwipeLightbox
    const pswps = document.querySelectorAll('.js-pswp-gallery') as NodeListOf<HTMLElement>;
    if (pswps.length) {
      pswps.forEach((pswp) => {
        const lightbox = new PhotoSwipeLightbox({
          gallery: pswp,
          children: 'a',
          showHideAnimationType: 'fade',
          pswpModule: () => import('photoswipe'),
        });
        lightbox.init();
      });
    }
  },
  resize() {
    adjustViewport();
    setRootProperty();
    fillWindowWidth();
  },
  breakPoint() {},
};

export { Common };
